import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import { formatDateToDDMMMYYYY } from '@/utils/dateFormat'
import { formatSkuDisplay } from '@/utils/skuFormat'
import { formatContractPrice } from '@/constants/contractCurrencies'
import { downloadContractFile } from '@/services/api/contracts'
import AISummaryModal from '@/components/ui/AISummaryModal'

interface ContractDetailsData {
  id?: string
  name: string
  ref?: string
  type: string
  supplier?: string
  contractType: string
  product?: string
  sku?: string
  qty: string
  price: string
  priceCurrency?: string
  terms?: string
  deliveryConditions?: string
  deliveryPeriod?: string
  buyContract?: string
  saleContract?: string
  dateModified: string
  customer?: string
  fileUrl?: string | null
  factory?: string
  incoterm?: string
  pol?: string
  pod?: string
  paymentTerms?: string
}

interface ContractDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  contractData?: ContractDetailsData
  onViewFile?: () => void
}

const ContractDetailsModal = ({ isOpen, onClose, contractData, onViewFile }: ContractDetailsModalProps) => {
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY
      // Prevent scrolling on body
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
      
      return () => {
        // Restore scrolling when modal closes
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  if (!isOpen || !contractData) return null

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ 
        zIndex: 60,
        overflow: 'hidden'
      }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '450px',
          maxWidth: '450px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Contract Details
          </h2>
          <div className="flex items-center gap-2">
            <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer circle */}
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              {/* Inner X - top-left to bottom-right */}
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Inner X - top-right to bottom-left */}
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          </div>
        </div>

        {/* Details Content */}
        <div 
          className="p-4 flex-1"
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start'
          }}
        >
          <div className="space-y-0">
            {/* Contract Number */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Contract Number
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                  {contractData.ref || contractData.name}
                </span>
            </div>

            {/* Contract Type */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Contract Type
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.contractType}
              </span>
            </div>

            {/* Supplier (Buy) / Customer (Sale) */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                {contractData.contractType === 'Buy Contract' ? 'Supplier' : 'Customer'}
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.contractType === 'Buy Contract'
                  ? (contractData.supplier || '-')
                  : (contractData.customer || '-')}
              </span>
            </div>

            {/* Product */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Product
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.product || '-'}
              </span>
            </div>

            {/* SKU */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                SKU
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.sku ? formatSkuDisplay(contractData.sku) : '-'}
              </span>
            </div>

            {/* Qty */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Qty
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.qty}
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Price
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {formatContractPrice(contractData.price, contractData.priceCurrency)}
              </span>
            </div>

            {/* Incoterm */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Incoterm
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.incoterm || '-'}
              </span>
            </div>

            {/* POL */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                POL
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.pol || '-'}
              </span>
            </div>

            {/* POD */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                POD
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.pod || '-'}
              </span>
            </div>

            {/* Delivery Conditions */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Delivery Conditions
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.deliveryConditions || '-'}
              </span>
            </div>

            {/* Shipping Period */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Shipping Period
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.deliveryPeriod || '-'}
              </span>
            </div>

            {/* Payment Terms */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Payment Terms
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {contractData.paymentTerms || '-'}
              </span>
            </div>

            {/* Buy/Sale Contract status */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                {contractData.contractType === 'Buy Contract' ? 'Buy Contract' : 'Sale Contract'}
              </span>
              {(() => {
                const status = contractData.contractType === 'Buy Contract' ? contractData.buyContract : contractData.saleContract
                const isUploaded = status === 'Uploaded' && !!contractData.fileUrl
                const badgeStyle = {
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: isUploaded ? '#13AB5B' : '#212126',
                  backgroundColor: isUploaded ? '#E8F7EF' : '#FFFFFF',
                  border: isUploaded ? '1px solid #13AB5B' : '1px solid #E9E9EA',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap' as const,
                  cursor: isUploaded && onViewFile ? 'pointer' : 'default',
                }
                if (isUploaded && onViewFile) {
                  return (
                    <button type="button" onClick={onViewFile} style={badgeStyle} title="View uploaded file">
                      {status}
                    </button>
                  )
                }
                return <span style={badgeStyle}>{status || 'Upload'}</span>
              })()}
            </div>

            {/* Date Modified */}
            <div className="flex items-center py-2 border-b border-gray-100" style={{ gap: '40px' }}>
              <span className="flex-shrink-0" style={{ fontFamily: 'Lexend Deca', fontWeight: 300, fontSize: '14px', lineHeight: '22px', color: '#69697A', width: '160px' }}>
                Date Modified
              </span>
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', lineHeight: '20px', color: '#212126' }}>
                {formatDateToDDMMMYYYY(contractData.dateModified)}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E9E9EA',
              borderRadius: '8px'
            }}
          >
            Cancel
          </button>
          {contractData.fileUrl && contractData.id && (
            <button
              type="button"
              disabled={downloadLoading}
              onClick={async () => {
                if (!contractData.id) return
                setDownloadLoading(true)
                try {
                  const baseName = contractData.name.split(' - ')[0] || 'contract'
                  await downloadContractFile(contractData.id, `${baseName}.pdf`)
                } catch {
                  window.alert('Could not download file. Please try again.')
                } finally {
                  setDownloadLoading(false)
                }
              }}
              className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                color: '#FFFFFF',
                backgroundColor: '#212126',
                borderRadius: '8px'
              }}
            >
              {downloadLoading ? 'Downloading…' : 'Download File'}
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(
    <>
      {modalContent}
      <AISummaryModal
        isOpen={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        entityType="contract"
        entityId={contractData.id ?? null}
      />
    </>,
    document.body
  )
}

export default ContractDetailsModal
